import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/Button';
import Slideover from '../ui/Slideover';
import { useToast } from '../ui/Toast';
import { WOCreateSheet } from '../workorders/WOCreateSheets';
import { WORangePicker } from '../workorders/WORangePicker';
import api, { settingsAPI } from '../../services/api';
import useSettingsList from '../../hooks/useSettingsList';
import useTechOptions from '../../hooks/useTechOptions';
import { DEFAULT_WO_TIMES, workOrderHasValidRange } from '../../utils/workOrderModel';
import { scheduleError, scheduleWrite } from '../../utils/woSchedule';

/**
 * IDI-871 D3: the sheets the Calendar's Unscheduled panel opens from a row's
 * "Schedule" button. CalendarPage mounts one only after "Schedule" (the panel
 * is closed first: one dialog at a time), and each calls `onDone({ saved })`
 * when it closes so the panel reopens (and reloads after a save).
 *
 * - A job: the Job page's Create Work Order (JobDetail.js), over the job as
 *   GET /v1/jobs/<id> returns it. No date is pre-filled; times are Settings'.
 * - A work order: "Schedule work order", a range picker saved through
 *   update_work_order with scheduleWrite's fields (as WorkOrderDetail's Edit).
 *   Never the calendar event API, never reschedule_work_order.
 */

const EMPTY = [];

// Settings' default start/stop times (useSchedulingDefaults' reader), read
// once before a sheet opens so the Create sheet seeds them on open.
async function readDefaultTimes() {
  try {
    const res = await settingsAPI.getQuoteDefaults();
    return {
      startTime: (res && res.wo_default_start_time) || DEFAULT_WO_TIMES.startTime,
      endTime: (res && res.wo_default_end_time) || DEFAULT_WO_TIMES.endTime,
    };
  } catch {
    return { ...DEFAULT_WO_TIMES };
  }
}

export function JobScheduleSheet({ jobId, creator = null, onDone }) {
  const toast = useToast();
  const [job, setJob] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const { items: crewItems } = useSettingsList('crews');
  const { items: resourceItems } = useSettingsList('resource_types');
  const { options: techOptions } = useTechOptions();
  // WOCreateSheet copies these into state whenever they change: keep them stable.
  const crews = useMemo(() => (Array.isArray(crewItems) ? crewItems : EMPTY), [crewItems]);
  const resources = useMemo(() => (Array.isArray(resourceItems) ? resourceItems : EMPTY), [resourceItems]);
  const techs = useMemo(() => (Array.isArray(techOptions) ? techOptions : EMPTY), [techOptions]);

  useEffect(() => {
    let active = true;
    Promise.all([api.getJob(jobId), readDefaultTimes()])
      .then(([data, times]) => {
        if (!active) return;
        if (!data) throw new Error('no job');
        setDefaults(times);
        setJob(data);
      })
      .catch(() => {
        if (!active) return;
        toast.error("Couldn't open this job. Try again.");
        onDone({ saved: false });
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const existingWorkOrders = useMemo(
    () => (job ? job.work_orders || job.workOrders || EMPTY : EMPTY),
    [job],
  );

  if (!job || !defaults) return null;

  return (
    <WOCreateSheet
      creator={creator}
      open
      onClose={() => onDone({ saved: false })}
      onSubmit={async (woData) => {
        const newWO = {
          id: woData.id || `wo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          ...woData,
        };
        // IDI-453: append server-side (create_work_order).
        await api.createWorkOrder(job.id || jobId, newWO);
        onDone({ saved: true });
      }}
      job={job}
      crews={crews}
      resources={resources}
      techs={techs}
      existingWorkOrders={existingWorkOrders}
      schedulingDefaults={defaults}
    />
  );
}

export function WorkOrderScheduleSheet({ jobId, workOrderId, onDone }) {
  const [range, setRange] = useState(null);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    readDefaultTimes().then((times) => {
      if (!active) return;
      setRange({ startDate: '', endDate: '', startTime: times.startTime, endTime: times.endTime });
    });
    return () => { active = false; };
  }, []);

  if (!range) return null;

  const validate = () => {
    if (!workOrderHasValidRange(range)) return 'A start and stop date are required';
    if (!range.endTime) return 'A stop time is required';
    return scheduleError({
      startDate: range.startDate,
      startTime: range.startTime,
      stopDate: range.endDate,
      stopTime: range.endTime,
    });
  };

  const handleSave = async () => {
    const msg = validate();
    setError(msg);
    setSaveError('');
    if (msg) return;
    setSaving(true);
    try {
      await api.updateWorkOrder(jobId, workOrderId, scheduleWrite({
        startDate: range.startDate,
        startTime: range.startTime,
        stopDate: range.endDate,
        stopTime: range.endTime,
      }));
      onDone({ saved: true });
    } catch {
      setSaveError("Couldn't save the schedule. Try again.");
      setSaving(false);
    }
  };

  const footer = (
    <div className="flex justify-end gap-2">
      <Button variant="secondary" onClick={() => onDone({ saved: false })}>Cancel</Button>
      <Button onClick={handleSave} loading={saving}>Save</Button>
    </div>
  );

  return (
    <Slideover
      open
      onClose={() => onDone({ saved: false })}
      title="Schedule work order"
      footer={footer}
      width="md"
    >
      <div className="space-y-4">
        <p className="text-sm text-gray-600">Pick a start and stop date and time.</p>
        <WORangePicker
          value={range}
          onChange={(next) => {
            setRange(next);
            if (error) setError('');
          }}
          error={error || undefined}
        />
        {saveError && (
          <div
            role="alert"
            className="bg-red-50 p-3 text-sm text-red-700"
            style={{ borderRadius: 'var(--radius-md)' }}
          >
            {saveError}
          </div>
        )}
      </div>
    </Slideover>
  );
}
